"use client"

import { useRef, useEffect, useCallback, useState } from "react"
import type { PCBStore } from "@/hooks/use-pcb-store"
import type { Point, DrawObject, LineStyle, LineEndSymbol, MarkerSymbol } from "@/lib/pcb-types"
import { LINE_STYLE_DASHES, PRESET_COLORS } from "@/lib/pcb-types"
import { Slider } from "@/components/ui/slider"

// Handle sizes for selection and vertex editing
const HANDLE_SIZE = 8
const MID_HANDLE_SIZE = 6
const HANDLE_HIT = 12  // Hit detection radius for handles

interface PCBCanvasProps {
  store: PCBStore
  selectedNet?: string | null
  blinkingNet?: string | null
}

export function PCBCanvas({ store, selectedNet, blinkingNet }: PCBCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false)
  const [currentPoints, setCurrentPoints] = useState<Point[]>([])
  const [drawPhase, setDrawPhase] = useState<"idle" | "first">("idle") // for two-click shapes

  // Panning
  const [isPanning, setIsPanning] = useState(false)
  const [panStart, setPanStart] = useState<Point>({ x: 0, y: 0 })

  // Image
  const [imageLoaded, setImageLoaded] = useState<HTMLImageElement | null>(null)

  // Blinking state for net highlight (toggles on/off during blink period)
  const [blinkVisible, setBlinkVisible] = useState(true)

  // Text input (for text tool)
  const [textInput, setTextInput] = useState<{
    x: number; y: number; visible: boolean
    mode: "text" | "editComment" | "editText"
    layerId?: string; objectId?: string
  }>({ x: 0, y: 0, visible: false, mode: "text" })
  const [textValue, setTextValue] = useState("")
  const textInputRef = useRef<HTMLInputElement>(null)

  // Object edit dialog (for double-click editing with Comment and Net)
  const [objectEditDialog, setObjectEditDialog] = useState<{
    visible: boolean
    x: number; y: number
    layerId: string; objectId: string
    comment: string; net: string
  } | null>(null)

  // Selection
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null)
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null)

  // Handle dragging (vertex + midpoint + comment + object move)
  const [dragHandle, setDragHandle] = useState<HandleInfo | null>(null)
  const [isDraggingComment, setIsDraggingComment] = useState(false)
  const [commentDragStart, setCommentDragStart] = useState<Point | null>(null)
  const [isDraggingObject, setIsDraggingObject] = useState(false)
  const [objectDragStart, setObjectDragStart] = useState<Point | null>(null)
  // Comment rotation dragging
  const [isDraggingCommentRotation, setIsDraggingCommentRotation] = useState(false)
  const [commentRotationCenter, setCommentRotationCenter] = useState<Point | null>(null)

  // Polyline/polygon in-progress points
  const [polyPoints, setPolyPoints] = useState<Point[]>([])
  const [polyPreview, setPolyPreview] = useState<Point | null>(null)
  const [shiftHeld, setShiftHeld] = useState(false)

  // Mouse position for cursor tracking
  const [mouseCanvas, setMouseCanvas] = useState<Point | null>(null)

  // Console message
  const [consoleMsg, setConsoleMsg] = useState("Welcome to PCB Reverse Engineering Tool. Import an image to begin.")

  // --- Track shift key ---
  useEffect(() => {
    const down = (e: KeyboardEvent) => { if (e.key === "Shift") setShiftHeld(true) }
    const up = (e: KeyboardEvent) => { if (e.key === "Shift") setShiftHeld(false) }
    window.addEventListener("keydown", down)
    window.addEventListener("keyup", up)
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up) }
  }, [])

  // Helper to get tool hints for console message
  const getToolHint = (tool: string, polyCount: number): string => {
    switch (tool) {
      case "select": return "Click to select objects. Drag to move."
      case "pan": return "Drag to pan the canvas."
      case "line": return "Click to set start point, then click for end point."
      case "polyline": return polyCount > 0 ? `${polyCount} points. Click to add, double-click/Enter to finish.` : "Click to start polyline."
      case "polygon": return polyCount > 0 ? `${polyCount} points. Click to add, double-click/Enter to close.` : "Click to start polygon."
      case "rect": return "Click to set corner, then click for opposite corner."
      case "circle": return "Click to set center, then click for radius."
      case "freehand": return "Click and drag to draw freehand."
      case "text": return "Click to place text."
      case "marker": return "Click to place marker."
      case "probe": return "Click to place probe tip."
      case "measure": return "Click to set start, then click for end point."
      default: return "Ready."
    }
  }

  // Update console message based on tool
  useEffect(() => {
    setConsoleMsg(getToolHint(store.activeTool, polyPoints.length))
  }, [store.activeTool, polyPoints.length])

  // Handle blinking effect when blinkingNet is active
  useEffect(() => {
    if (!blinkingNet) {
      setBlinkVisible(true)
      return
    }
    // Toggle visibility every 200ms for blinking effect
    const interval = setInterval(() => {
      setBlinkVisible(prev => !prev)
    }, 200)
    return () => clearInterval(interval)
  }, [blinkingNet])

  // Prevent browser zoom on Ctrl+scroll when cursor is on canvas
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const preventBrowserZoom = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
      }
    }
    canvas.addEventListener("wheel", preventBrowserZoom, { passive: false })
    return () => canvas.removeEventListener("wheel", preventBrowserZoom)
  }, [])

  // --- Load PCB image and center it ---
  useEffect(() => {
    if (store.pcbImage) {
      const img = new Image()
      img.crossOrigin = "anonymous"
      img.onload = () => {
        setImageLoaded(img)
        const container = containerRef.current
        if (container) {
          store.resetViewToFit(container.clientWidth, container.clientHeight, img.width, img.height)
        }
        setConsoleMsg(`Image loaded: ${img.width}x${img.height}px`)
      }
      img.src = store.pcbImage
    } else {
      setImageLoaded(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.pcbImage])

  // --- Coordinate conversion ---
  const screenToCanvas = useCallback(
    (screenX: number, screenY: number): Point => {
      const canvas = canvasRef.current
      if (!canvas) return { x: 0, y: 0 }
      const rect = canvas.getBoundingClientRect()
      return {
        x: (screenX - rect.left - store.panOffset.x) / store.zoom,
        y: (screenY - rect.top - store.panOffset.y) / store.zoom,
      }
    },
    [store.panOffset, store.zoom]
  )

  // Snap to 0/45/90/135/180/225/270/315 degrees
  const snapAngle = useCallback((origin: Point, target: Point): Point => {
    if (!shiftHeld) return target
    const dx = target.x - origin.x
    const dy = target.y - origin.y
    const d = Math.sqrt(dx * dx + dy * dy)
    if (d < 1) return target
    const angle = Math.atan2(dy, dx)
    const snap45 = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4)
    return { x: origin.x + Math.cos(snap45) * d, y: origin.y + Math.sin(snap45) * d }
  }, [shiftHeld])

  // --- Helpers ---
  function dist(a: Point, b: Point) { return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2) }

  function pointNearLine(p: Point, a: Point, b: Point, threshold: number): boolean {
    const lenSq = (b.x - a.x) ** 2 + (b.y - a.y) ** 2
    if (lenSq === 0) return dist(p, a) < threshold
    let t = ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / lenSq
    t = Math.max(0, Math.min(1, t))
    const proj = { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) }
    return dist(p, proj) < threshold
  }

  function hitTestObject(obj: DrawObject, canvasPoint: Point, threshold: number): boolean {
    switch (obj.type) {
      case "line":
      case "measure":
        return pointNearLine(canvasPoint, obj.points[0], obj.points[1], threshold)
      case "polyline":
        for (let i = 0; i < obj.points.length - 1; i++) {
          if (pointNearLine(canvasPoint, obj.points[i], obj.points[i + 1], threshold)) return true
        }
        return false
      case "polygon": {
        // Check edges first
        for (let i = 0; i < obj.points.length; i++) {
          const next = (i + 1) % obj.points.length
          if (pointNearLine(canvasPoint, obj.points[i], obj.points[next], threshold)) return true
        }
        // If filled, also check if point is inside polygon (ray casting)
        if (obj.filled && obj.points.length >= 3) {
          let inside = false
          for (let i = 0, j = obj.points.length - 1; i < obj.points.length; j = i++) {
            const xi = obj.points[i].x, yi = obj.points[i].y
            const xj = obj.points[j].x, yj = obj.points[j].y
            if (((yi > canvasPoint.y) !== (yj > canvasPoint.y)) &&
              (canvasPoint.x < (xj - xi) * (canvasPoint.y - yi) / (yj - yi) + xi)) {
              inside = !inside
            }
          }
          if (inside) return true
        }
        return false
      }
      case "rect": {
        const [p0, p1] = obj.points
        const minX = Math.min(p0.x, p1.x), maxX = Math.max(p0.x, p1.x)
        const minY = Math.min(p0.y, p1.y), maxY = Math.max(p0.y, p1.y)
        // If filled, check if point is inside rect
        if (obj.filled && canvasPoint.x >= minX && canvasPoint.x <= maxX && canvasPoint.y >= minY && canvasPoint.y <= maxY) {
          return true
        }
        const corners = [{ x: minX, y: minY }, { x: maxX, y: minY }, { x: maxX, y: maxY }, { x: minX, y: maxY }]
        for (let i = 0; i < 4; i++) { if (pointNearLine(canvasPoint, corners[i], corners[(i + 1) % 4], threshold)) return true }
        return false
      }
      case "circle": {
        const center = obj.points[0]
        const dx = obj.points[1].x - center.x, dy = obj.points[1].y - center.y
        const r = Math.sqrt(dx * dx + dy * dy)
        // If filled, check if point is inside circle
        if (obj.filled && dist(canvasPoint, center) <= r) return true
        return Math.abs(dist(canvasPoint, center) - r) < threshold
      }
      case "freehand":
        for (let i = 0; i < obj.points.length - 1; i++) {
          if (pointNearLine(canvasPoint, obj.points[i], obj.points[i + 1], threshold)) return true
        }
        return false
      case "text":
        return dist(canvasPoint, obj.points[0]) < threshold * 3
      case "marker": {
        const markerSize = obj.markerSize ?? 12
        return dist(canvasPoint, obj.points[0]) < Math.max(markerSize, threshold)
      }
      case "probe": {
        // Hit test along entire probe body line (head to tail) with wider threshold
        if (obj.points.length >= 2) {
          // Use wider threshold (12px) to allow easy body dragging
          return pointNearLine(canvasPoint, obj.points[0], obj.points[1], Math.max(threshold * 3, 12))
        }
        return dist(canvasPoint, obj.points[0]) < threshold
      }
    }
    return false
  }

  // Find selected object
  const getSelectedObject = useCallback((): DrawObject | null => {
    if (!selectedObjectId || !selectedLayerId) return null
    const layer = store.layers.find((l) => l.id === selectedLayerId)
    return layer?.objects.find((o) => o.id === selectedObjectId) ?? null
  }, [selectedObjectId, selectedLayerId, store.layers])

  // --- Set line dash ---
  function applyLineDash(ctx: CanvasRenderingContext2D, style: LineStyle | undefined, strokeWidth: number) {
    const dashes = LINE_STYLE_DASHES[style ?? "solid"]
    if (dashes.length > 0) { ctx.setLineDash(dashes.map((d) => d * strokeWidth)) }
    else { ctx.setLineDash([]) }
  }

  // --- Universal comment box configuration ---
  // Returns default properties for comment boxes based on object type
  function getCommentBoxConfig(obj: DrawObject): {
    anchorBase: Point
    defaultOffX: number
    defaultOffY: number
    defaultFontSize: number
    padding: number
    labelText: string
    useSmartAnchor: boolean
  } {
    // Get anchor base point - for probe, use tail (second point) as anchor base
    const anchorBase = obj.type === "probe" && obj.points.length >= 2 ? obj.points[1] : obj.points[0]

    // Default values for all object types
    const defaultOffX = 15
    const defaultOffY = -25
    const defaultFontSize = 20
    const padding = 4
    const useSmartAnchor = true

    // Get label text from comment
    const labelText = obj.comment ?? ""

    return { anchorBase, defaultOffX, defaultOffY, defaultFontSize, padding, labelText, useSmartAnchor }
  }

  function darkenColor(hex: string, factor: number): string {
    const c = hex.replace("#", "")
    const r = Math.round(parseInt(c.substring(0, 2), 16) * factor)
    const g = Math.round(parseInt(c.substring(2, 4), 16) * factor)
    const b = Math.round(parseInt(c.substring(4, 6), 16) * factor)
    return `rgb(${r},${g},${b})`
  }

  // Draw line end symbol at a point with given angle (direction pointing away from line)
  // Symbol is centered at the endpoint for both single and double stroke
  function drawLineEndSymbol(ctx: CanvasRenderingContext2D, symbol: string | undefined, pt: Point, angle: number, color: string, size: number = 8) {
    if (!symbol || symbol === "none") return
    ctx.save()
    ctx.translate(pt.x, pt.y)
    ctx.rotate(angle)
    ctx.fillStyle = color
    ctx.strokeStyle = color
    ctx.lineWidth = 1.5
    // All symbols centered at origin (0,0) which is the edge point
    const hs = size / 2 // half size for centering
    switch (symbol) {
      case "arrow":
        ctx.beginPath()
        ctx.moveTo(hs, 0)
        ctx.lineTo(-hs, -hs)
        ctx.moveTo(hs, 0)
        ctx.lineTo(-hs, hs)
        ctx.stroke()
        break
      case "filled_arrow":
        ctx.beginPath()
        ctx.moveTo(hs, 0)
        ctx.lineTo(-hs, -hs)
        ctx.lineTo(-hs, hs)
        ctx.closePath()
        ctx.fill()
        break
      case "double_arrows":
        ctx.beginPath()
        ctx.moveTo(hs, 0); ctx.lineTo(-hs * 0.3, -hs); ctx.moveTo(hs, 0); ctx.lineTo(-hs * 0.3, hs)
        ctx.moveTo(0, 0); ctx.lineTo(-hs, -hs); ctx.moveTo(0, 0); ctx.lineTo(-hs, hs)
        ctx.stroke()
        break
      case "filled_square":
        ctx.fillRect(-hs, -hs, size, size)
        break
      case "square":
        ctx.strokeRect(-hs, -hs, size, size)
        break
      case "filled_circle":
        ctx.beginPath(); ctx.arc(0, 0, hs, 0, Math.PI * 2); ctx.fill()
        break
      case "circle":
        ctx.beginPath(); ctx.arc(0, 0, hs, 0, Math.PI * 2); ctx.stroke()
        break
      case "filled_losange":
      case "losange": {
        const isFilled = symbol === "filled_losange"
        ctx.beginPath()
        ctx.moveTo(hs, 0)
        ctx.lineTo(0, -hs)
        ctx.lineTo(-hs, 0)
        ctx.lineTo(0, hs)
        ctx.closePath()
        if (isFilled) ctx.fill()
        else ctx.stroke()
        break
      }
      case "chevron_right":
        ctx.beginPath()
        ctx.moveTo(-hs, -hs)
        ctx.lineTo(hs, 0)
        ctx.lineTo(-hs, hs)
        ctx.stroke()
        break
      case "chevron_left":
        ctx.beginPath()
        ctx.moveTo(hs, -hs)
        ctx.lineTo(-hs, 0)
        ctx.lineTo(hs, hs)
        ctx.stroke()
        break
      case "x":
        ctx.beginPath()
        ctx.moveTo(-hs, -hs); ctx.lineTo(hs, hs)
        ctx.moveTo(hs, -hs); ctx.lineTo(-hs, hs)
        ctx.stroke()
        break
    }
    ctx.restore()
  }

  // Compute smart comment wire endpoint (closest point on object to comment box)
  function getCommentAnchor(obj: DrawObject, commentBox: Point): Point {
    if (obj.points.length === 0) return { x: 0, y: 0 }
    // Marker: connect to edge of marker symbol based on size
    if (obj.type === "marker") {
      const mc = obj.points[0]
      const mSize = obj.markerSize ?? 12
      const hs = mSize / 2
      const angle = Math.atan2(commentBox.y - mc.y, commentBox.x - mc.x)
      return { x: mc.x + Math.cos(angle) * hs, y: mc.y + Math.sin(angle) * hs }
    }
    // Probe: connect to the tail (second point)
    if (obj.type === "probe" && obj.points.length >= 2) {
      return obj.points[1] // tail position
    }
    // Circle: connect to perimeter (item 4)
    if (obj.type === "circle" && obj.points.length >= 2) {
      const cc = obj.points[0]
      const r = Math.sqrt((obj.points[1].x - cc.x) ** 2 + (obj.points[1].y - cc.y) ** 2)
      const angle = Math.atan2(commentBox.y - cc.y, commentBox.x - cc.x)
      return { x: cc.x + Math.cos(angle) * r, y: cc.y + Math.sin(angle) * r }
    }
    // Rect: closest point on perimeter
    if (obj.type === "rect" && obj.points.length >= 2) {
      const [p0, p1] = obj.points
      const minX = Math.min(p0.x, p1.x), maxX = Math.max(p0.x, p1.x)
      const minY = Math.min(p0.y, p1.y), maxY = Math.max(p0.y, p1.y)
      const corners = [{ x: minX, y: minY }, { x: maxX, y: minY }, { x: maxX, y: maxY }, { x: minX, y: maxY }]
      let best = corners[0], bestD = Infinity
      for (let i = 0; i < 4; i++) {
        const a = corners[i], b = corners[(i + 1) % 4]
        const lenSq = (b.x - a.x) ** 2 + (b.y - a.y) ** 2
        let t = 0
        if (lenSq > 0) t = Math.max(0, Math.min(1, ((commentBox.x - a.x) * (b.x - a.x) + (commentBox.y - a.y) * (b.y - a.y)) / lenSq))
        const proj = { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) }
        const d = dist(proj, commentBox)
        if (d < bestD) { bestD = d; best = proj }
      }
      return best
    }
    // For multi-point objects, find the closest point on any segment
    if (obj.type === "polyline" || obj.type === "polygon" || obj.type === "freehand" || obj.type === "line" || obj.type === "measure") {
      let bestDist = Infinity
      let bestPt = obj.points[0]
      const isClosed = obj.type === "polygon"
      const segs = isClosed ? obj.points.length : obj.points.length - 1
      for (let i = 0; i < segs; i++) {
        const a = obj.points[i]
        const b = obj.points[(i + 1) % obj.points.length]
        const lenSq = (b.x - a.x) ** 2 + (b.y - a.y) ** 2
        let t = 0
        if (lenSq > 0) t = Math.max(0, Math.min(1, ((commentBox.x - a.x) * (b.x - a.x) + (commentBox.y - a.y) * (b.y - a.y)) / lenSq))
        const proj = { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) }
        const d = dist(proj, commentBox)
        if (d < bestDist) { bestDist = d; bestPt = proj }
      }
      return bestPt
    }
    // Fallback: closest vertex
    let bestDist = Infinity
    let bestPt = obj.points[0]
    for (const p of obj.points) {
      const d = dist(p, commentBox)
      if (d < bestDist) { bestDist = d; bestPt = p }
    }
    return bestPt
  }

  // --- DRAW FRAME ---
  const draw = useCallback(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    canvas.width = container.clientWidth
    canvas.height = container.clientHeight

    if (imageLoaded) {
      canvas.setAttribute("data-img-width", String(imageLoaded.width))
      canvas.setAttribute("data-img-height", String(imageLoaded.height))
    }

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    // Clear
    ctx.fillStyle = "#1a1a2e"
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    ctx.save()
    ctx.translate(store.panOffset.x, store.panOffset.y)
    ctx.scale(store.zoom, store.zoom)

    // Grid (subtly visible)
    ctx.strokeStyle = "rgba(255,255,255,0.08)"
    ctx.lineWidth = 0.5
    const gridSize = 50
    const viewLeft = -store.panOffset.x / store.zoom
    const viewTop = -store.panOffset.y / store.zoom
    const viewRight = viewLeft + canvas.width / store.zoom
    const viewBottom = viewTop + canvas.height / store.zoom
    const startX = Math.floor(viewLeft / gridSize) * gridSize
    const startY = Math.floor(viewTop / gridSize) * gridSize
    for (let x = startX; x <= viewRight; x += gridSize) {
      ctx.beginPath(); ctx.moveTo(x, viewTop); ctx.lineTo(x, viewBottom); ctx.stroke()
    }
    for (let y = startY; y <= viewBottom; y += gridSize) {
      ctx.beginPath(); ctx.moveTo(viewLeft, y); ctx.lineTo(viewRight, y); ctx.stroke()
    }

    // PCB image with grayscale, hue shift, and color tint
    if (imageLoaded) {
      // Create offscreen canvas for compositing with transparency support
      const offCanvas = document.createElement("canvas")
      offCanvas.width = imageLoaded.width
      offCanvas.height = imageLoaded.height
      const offCtx = offCanvas.getContext("2d", { willReadFrequently: false })!

      // Apply filters and draw original image
      const filters: string[] = []
      if (store.imageGrayscale > 0) filters.push(`grayscale(${store.imageGrayscale}%)`)
      if (store.imageTint > 0) filters.push(`hue-rotate(${store.imageTint}deg)`)
      if (filters.length > 0) offCtx.filter = filters.join(" ")
      offCtx.drawImage(imageLoaded, 0, 0)
      offCtx.filter = "none"

      // Color tint overlay - respects PNG transparency
      // Uses multiply blend then masks with original alpha channel
      if (store.imageOverlay && store.imageOverlayOpacity > 0) {
        // Create a second offscreen canvas for the tint layer
        const tintCanvas = document.createElement("canvas")
        tintCanvas.width = imageLoaded.width
        tintCanvas.height = imageLoaded.height
        const tintCtx = tintCanvas.getContext("2d")!

        // Draw original image to get alpha mask
        tintCtx.drawImage(imageLoaded, 0, 0)

        // Apply color using source-atop (only draws where pixels exist)
        tintCtx.globalCompositeOperation = "source-atop"
        tintCtx.fillStyle = store.imageOverlay
        tintCtx.fillRect(0, 0, imageLoaded.width, imageLoaded.height)

        // Blend tint onto the filtered image with opacity
        offCtx.globalAlpha = store.imageOverlayOpacity / 100
        offCtx.globalCompositeOperation = "multiply"
        offCtx.drawImage(tintCanvas, 0, 0)

        // Restore original alpha channel from source image
        offCtx.globalAlpha = 1
        offCtx.globalCompositeOperation = "destination-in"
        offCtx.drawImage(imageLoaded, 0, 0)
      }

      // Draw composited result to main canvas
      ctx.drawImage(offCanvas, 0, 0)
    }

    // Draw objects per layer
    for (const layer of store.layers) {
      if (!layer.visible) continue
      ctx.globalAlpha = layer.opacity

      for (const obj of layer.objects) {
        // Skip hidden objects
        if (obj.visible === false) continue

        // Handle blinking net mode: hide non-matching objects, blink matching ones
        if (blinkingNet) {
          const isBlinkingNetMatch = obj.net === blinkingNet
          if (!isBlinkingNetMatch) continue // Hide objects that don't match the blinking net
          if (!blinkVisible) continue // During "off" phase of blink, skip rendering matching objects too
        }

        // Apply highlight glow if object matches selected net
        const isNetHighlighted = selectedNet && obj.net === selectedNet
        if (isNetHighlighted) {
          ctx.shadowColor = "#ffffff"  // white for clear visibility
          ctx.shadowBlur = 20
          ctx.shadowOffsetX = 0
          ctx.shadowOffsetY = 0
        } else {
          ctx.shadowColor = "transparent"
          ctx.shadowBlur = 0
        }

        ctx.strokeStyle = obj.color
        ctx.fillStyle = obj.color
        ctx.lineWidth = obj.strokeWidth
        ctx.lineCap = "round"
        ctx.lineJoin = "round"
        applyLineDash(ctx, obj.lineStyle, obj.strokeWidth)

        switch (obj.type) {
          case "line":
            ctx.beginPath()
            ctx.moveTo(obj.points[0].x, obj.points[0].y)
            ctx.lineTo(obj.points[1].x, obj.points[1].y)
            ctx.stroke()
            break

          case "polyline":
            if (obj.points.length >= 2) {
              if (obj.lineBodyStyle === "double") {
                // Double line - compute offset points using line intersection at corners
                const offset = obj.strokeWidth
                // Helper to get perpendicular normal (left side when sign=1)
                const getPerp = (p1: Point, p2: Point, d: number) => {
                  const dx = p2.x - p1.x, dy = p2.y - p1.y
                  const len = Math.hypot(dx, dy) || 1
                  return { x: -dy / len * d, y: dx / len * d }
                }
                // Helper to find intersection of two lines (p1+t*d1) and (p2+t*d2)
                const lineIntersect = (p1: Point, d1: Point, p2: Point, d2: Point): Point => {
                  const cross = d1.x * d2.y - d1.y * d2.x
                  if (Math.abs(cross) < 0.0001) return p1 // parallel, use first point
                  const t = ((p2.x - p1.x) * d2.y - (p2.y - p1.y) * d2.x) / cross
                  return { x: p1.x + t * d1.x, y: p1.y + t * d1.y }
                }
                for (const sign of [-1, 1]) {
                  const offsetPts: Point[] = []
                  for (let i = 0; i < obj.points.length; i++) {
                    const curr = obj.points[i]
                    if (i === 0) {
                      const n = getPerp(curr, obj.points[1], offset * sign)
                      offsetPts.push({ x: curr.x + n.x, y: curr.y + n.y })
                    } else if (i === obj.points.length - 1) {
                      const n = getPerp(obj.points[i - 1], curr, offset * sign)
                      offsetPts.push({ x: curr.x + n.x, y: curr.y + n.y })
                    } else {
                      const prev = obj.points[i - 1], next = obj.points[i + 1]
                      const n1 = getPerp(prev, curr, offset * sign)
                      const n2 = getPerp(curr, next, offset * sign)
                      // Offset line 1: from prev+n1 in direction (curr-prev)
                      const p1 = { x: prev.x + n1.x, y: prev.y + n1.y }
                      const d1 = { x: curr.x - prev.x, y: curr.y - prev.y }
                      // Offset line 2: from curr+n2 in direction (next-curr)
                      const p2 = { x: curr.x + n2.x, y: curr.y + n2.y }
                      const d2 = { x: next.x - curr.x, y: next.y - curr.y }
                      offsetPts.push(lineIntersect(p1, d1, p2, d2))
                    }
                  }
                  ctx.beginPath()
                  ctx.moveTo(offsetPts[0].x, offsetPts[0].y)
                  for (let i = 1; i < offsetPts.length; i++) ctx.lineTo(offsetPts[i].x, offsetPts[i].y)
                  ctx.stroke()
                }
              } else {
                ctx.beginPath()
                ctx.moveTo(obj.points[0].x, obj.points[0].y)
                for (let i = 1; i < obj.points.length; i++) ctx.lineTo(obj.points[i].x, obj.points[i].y)
                ctx.stroke()
              }
              // End symbol size - larger for double stroke to cover both lines
              const symbolSize = obj.lineBodyStyle === "double" ? obj.strokeWidth * 5 : obj.strokeWidth * 3
              // Tail symbol (at first point - center axis for double lines)
              if (obj.tailSymbol && obj.tailSymbol !== "none") {
                const p0 = obj.points[0], p1 = obj.points[1]
                const angle = Math.atan2(p0.y - p1.y, p0.x - p1.x)
                drawLineEndSymbol(ctx, obj.tailSymbol, p0, angle, obj.color, symbolSize)
              }
              // Head symbol (at last point - center axis for double lines)
              if (obj.headSymbol && obj.headSymbol !== "none") {
                const pn = obj.points[obj.points.length - 1], pn1 = obj.points[obj.points.length - 2]
                const angle = Math.atan2(pn.y - pn1.y, pn.x - pn1.x)
                drawLineEndSymbol(ctx, obj.headSymbol, pn, angle, obj.color, symbolSize)
              }
            }
            break

          case "polygon":
            if (obj.points.length >= 3) {
              ctx.beginPath()
              ctx.moveTo(obj.points[0].x, obj.points[0].y)
              for (let i = 1; i < obj.points.length; i++) ctx.lineTo(obj.points[i].x, obj.points[i].y)
              ctx.closePath()
              if (obj.filled) {
                ctx.globalAlpha = layer.opacity * ((obj.fillOpacity ?? 50) / 100)
                ctx.fill()
              }
              ctx.globalAlpha = layer.opacity
              if (obj.lineBodyStyle === "double") {
                // Double line - compute offset points using line intersection at corners
                const offset = obj.strokeWidth
                const getPerp = (p1: Point, p2: Point, d: number) => {
                  const dx = p2.x - p1.x, dy = p2.y - p1.y
                  const len = Math.hypot(dx, dy) || 1
                  return { x: -dy / len * d, y: dx / len * d }
                }
                const lineIntersect = (p1: Point, d1: Point, p2: Point, d2: Point): Point => {
                  const cross = d1.x * d2.y - d1.y * d2.x
                  if (Math.abs(cross) < 0.0001) return p1
                  const t = ((p2.x - p1.x) * d2.y - (p2.y - p1.y) * d2.x) / cross
                  return { x: p1.x + t * d1.x, y: p1.y + t * d1.y }
                }
                for (const sign of [-1, 1]) {
                  const offsetPts: Point[] = []
                  for (let i = 0; i < obj.points.length; i++) {
                    const prev = obj.points[(i - 1 + obj.points.length) % obj.points.length]
                    const curr = obj.points[i]
                    const next = obj.points[(i + 1) % obj.points.length]
                    const n1 = getPerp(prev, curr, offset * sign)
                    const n2 = getPerp(curr, next, offset * sign)
                    const p1 = { x: prev.x + n1.x, y: prev.y + n1.y }
                    const d1 = { x: curr.x - prev.x, y: curr.y - prev.y }
                    const p2 = { x: curr.x + n2.x, y: curr.y + n2.y }
                    const d2 = { x: next.x - curr.x, y: next.y - curr.y }
                    offsetPts.push(lineIntersect(p1, d1, p2, d2))
                  }
                  ctx.beginPath()
                  ctx.moveTo(offsetPts[0].x, offsetPts[0].y)
                  for (let i = 1; i < offsetPts.length; i++) ctx.lineTo(offsetPts[i].x, offsetPts[i].y)
                  ctx.closePath()
                  ctx.stroke()
                }
              } else {
                ctx.stroke()
              }
            }
            break

          case "rect": {
            const [rp0, rp1] = obj.points
            const rx = Math.min(rp0.x, rp1.x), ry = Math.min(rp0.y, rp1.y)
            const rw = Math.abs(rp1.x - rp0.x), rh = Math.abs(rp1.y - rp0.y)
            if (obj.filled) {
              ctx.globalAlpha = layer.opacity * ((obj.fillOpacity ?? 50) / 100)
              ctx.fillRect(rx, ry, rw, rh)
            }
            ctx.globalAlpha = layer.opacity
            if (obj.lineBodyStyle === "double") {
              // Double line - two parallel rects spaced by one strokeWidth
              const offset = obj.strokeWidth
              ctx.strokeRect(rx - offset, ry - offset, rw + offset * 2, rh + offset * 2)
              ctx.strokeRect(rx + offset, ry + offset, rw - offset * 2, rh - offset * 2)
            } else {
              ctx.strokeRect(rx, ry, rw, rh)
            }
            break
          }

          case "circle": {
            const cc = obj.points[0]
            const cr = Math.sqrt((obj.points[1].x - cc.x) ** 2 + (obj.points[1].y - cc.y) ** 2)
            ctx.beginPath(); ctx.arc(cc.x, cc.y, cr, 0, Math.PI * 2)
            if (obj.filled) {
              ctx.globalAlpha = layer.opacity * ((obj.fillOpacity ?? 50) / 100)
              ctx.fill()
            }
            ctx.globalAlpha = layer.opacity
            if (obj.lineBodyStyle === "double") {
              // Double line - two parallel circles spaced by one strokeWidth
              const offset = obj.strokeWidth
              ctx.beginPath(); ctx.arc(cc.x, cc.y, cr - offset, 0, Math.PI * 2); ctx.stroke()
              ctx.beginPath(); ctx.arc(cc.x, cc.y, cr + offset, 0, Math.PI * 2); ctx.stroke()
            } else {
              ctx.stroke()
            }
            break
          }

          case "freehand":
            if (obj.points.length >= 2) {
              ctx.beginPath()
              if (obj.lineBodyStyle === "double") {
                // Double line - compute offset points using line intersection at corners
                const offset = obj.strokeWidth
                const getPerp = (p1: Point, p2: Point, d: number) => {
                  const dx = p2.x - p1.x, dy = p2.y - p1.y
                  const len = Math.hypot(dx, dy) || 1
                  return { x: -dy / len * d, y: dx / len * d }
                }
                const lineIntersect = (p1: Point, d1: Point, p2: Point, d2: Point): Point => {
                  const cross = d1.x * d2.y - d1.y * d2.x
                  if (Math.abs(cross) < 0.0001) return p1
                  const t = ((p2.x - p1.x) * d2.y - (p2.y - p1.y) * d2.x) / cross
                  return { x: p1.x + t * d1.x, y: p1.y + t * d1.y }
                }
                for (const sign of [-1, 1]) {
                  const offsetPts: Point[] = []
                  for (let i = 0; i < obj.points.length; i++) {
                    const curr = obj.points[i]
                    if (i === 0) {
                      const n = getPerp(curr, obj.points[1], offset * sign)
                      offsetPts.push({ x: curr.x + n.x, y: curr.y + n.y })
                    } else if (i === obj.points.length - 1) {
                      const n = getPerp(obj.points[i - 1], curr, offset * sign)
                      offsetPts.push({ x: curr.x + n.x, y: curr.y + n.y })
                    } else {
                      const prev = obj.points[i - 1], next = obj.points[i + 1]
                      const n1 = getPerp(prev, curr, offset * sign)
                      const n2 = getPerp(curr, next, offset * sign)
                      const p1 = { x: prev.x + n1.x, y: prev.y + n1.y }
                      const d1 = { x: curr.x - prev.x, y: curr.y - prev.y }
                      const p2 = { x: curr.x + n2.x, y: curr.y + n2.y }
                      const d2 = { x: next.x - curr.x, y: next.y - curr.y }
                      offsetPts.push(lineIntersect(p1, d1, p2, d2))
                    }
                  }
                  ctx.beginPath()
                  ctx.moveTo(offsetPts[0].x, offsetPts[0].y)
                  for (let i = 1; i < offsetPts.length; i++) ctx.lineTo(offsetPts[i].x, offsetPts[i].y)
                  ctx.stroke()
                }
              } else {
                ctx.moveTo(obj.points[0].x, obj.points[0].y)
                for (let i = 1; i < obj.points.length; i++) ctx.lineTo(obj.points[i].x, obj.points[i].y)
                ctx.stroke()
              }
              // End symbol size - larger for double stroke to cover both lines
              const freehandSymbolSize = obj.lineBodyStyle === "double" ? obj.strokeWidth * 5 : obj.strokeWidth * 3
              // Tail symbol (at first point - center axis for double lines)
              if (obj.tailSymbol && obj.tailSymbol !== "none" && obj.points.length >= 2) {
                const p0 = obj.points[0], p1 = obj.points[1]
                const angle = Math.atan2(p0.y - p1.y, p0.x - p1.x)
                drawLineEndSymbol(ctx, obj.tailSymbol, p0, angle, obj.color, freehandSymbolSize)
              }
              // Head symbol (at last point - center axis for double lines)
              if (obj.headSymbol && obj.headSymbol !== "none" && obj.points.length >= 2) {
                const pn = obj.points[obj.points.length - 1], pn1 = obj.points[obj.points.length - 2]
                const angle = Math.atan2(pn.y - pn1.y, pn.x - pn1.x)
                drawLineEndSymbol(ctx, obj.headSymbol, pn, angle, obj.color, freehandSymbolSize)
              }
            }
            break

          case "text": {
            ctx.setLineDash([])
            const fontSize = obj.fontSize ?? Math.max(12, obj.strokeWidth * 6)
            const ff = obj.fontFamily ?? "monospace"
            const bold = obj.fontBold ? "bold " : ""
            const italic = obj.fontItalic ? "italic " : ""
            ctx.font = `${italic}${bold}${fontSize}px ${ff}`
            ctx.fillStyle = obj.color
            ctx.textBaseline = "top"
            const txt = obj.text ?? ""
            ctx.fillText(txt, obj.points[0].x, obj.points[0].y)
            if (obj.fontUnderline) {
              const m = ctx.measureText(txt)
              ctx.strokeStyle = obj.color
              ctx.lineWidth = 1
              ctx.beginPath()
              ctx.moveTo(obj.points[0].x, obj.points[0].y + fontSize + 1)
              ctx.lineTo(obj.points[0].x + m.width, obj.points[0].y + fontSize + 1)
              ctx.stroke()
            }
            break
          }

          case "marker": {
            ctx.setLineDash([])
            const mx = obj.points[0].x
            const my = obj.points[0].y
            const mSize = obj.markerSize ?? 12
            const mSymbol = obj.markerSymbol ?? "circle"
            const mFilled = obj.markerFilled ?? true
            const hs = mSize / 2

            ctx.fillStyle = obj.color
            ctx.strokeStyle = obj.color
            ctx.lineWidth = 1.5

            switch (mSymbol) {
              case "circle":
                ctx.beginPath()
                ctx.arc(mx, my, hs, 0, Math.PI * 2)
                if (mFilled) ctx.fill()
                else ctx.stroke()
                break
              case "rectangle":
                if (mFilled) {
                  ctx.fillRect(mx - hs, my - hs, mSize, mSize)
                } else {
                  ctx.strokeRect(mx - hs, my - hs, mSize, mSize)
                }
                break
              case "diamond":
                ctx.beginPath()
                ctx.moveTo(mx, my - hs)
                ctx.lineTo(mx + hs, my)
                ctx.lineTo(mx, my + hs)
                ctx.lineTo(mx - hs, my)
                ctx.closePath()
                if (mFilled) ctx.fill()
                else ctx.stroke()
                break
            }
            break
          }

          case "probe": {
            ctx.setLineDash([])
            if (obj.points.length < 2) break
            const head = obj.points[0] // tip position
            const tail = obj.points[1] // tail position (at extreme end of probe)
            const probeSize = obj.probeSize ?? 90 // Default 90px
            
            // Calculate angle from tail to head
            const angle = Math.atan2(head.y - tail.y, head.x - tail.x)
            
            ctx.save()
            ctx.translate(head.x, head.y)
            ctx.rotate(angle + Math.PI / 2) // Rotate so probe points toward head
            
            // Original SVG proportions (total height ~153px from tip to cap end):
            // Tip: 40px, Upper grip: 12px, Middle: 7px, Hatched body: 25px, Solid body: 60px, Cap: 9px
            // Scale based on probeSize (probeSize controls total visual length)
            const scale = probeSize / 153
            
            // All stroke outlines are BLACK
            ctx.strokeStyle = "#000000"
            ctx.lineWidth = 1
            ctx.lineJoin = "round"
            
            // Body gradient (colored based on obj.color)
            const bodyGradient = ctx.createLinearGradient(-8 * scale, 0, 8 * scale, 0)
            bodyGradient.addColorStop(0, darkenColor(obj.color, 0.6))
            bodyGradient.addColorStop(0.5, obj.color)
            bodyGradient.addColorStop(1, darkenColor(obj.color, 0.6))
            
            // Gold tip gradient
            const tipGradient = ctx.createLinearGradient(-4 * scale, 0, 4 * scale, 0)
            tipGradient.addColorStop(0, "#b8860b")
            tipGradient.addColorStop(0.5, "#ffd700")
            tipGradient.addColorStop(1, "#daa520")
            
            // 1. Gold tip (triangle) - 40px in original
            ctx.fillStyle = tipGradient
            ctx.beginPath()
            ctx.moveTo(0, 0) // tip point
            ctx.lineTo(-4 * scale, 40 * scale)
            ctx.lineTo(4 * scale, 40 * scale)
            ctx.closePath()
            ctx.fill()
            ctx.stroke()
            
            // 2. Upper grip section - 12px height at y=40-52
            ctx.fillStyle = bodyGradient
            ctx.beginPath()
            ctx.roundRect(-7 * scale, 40 * scale, 14 * scale, 12 * scale, 2 * scale)
            ctx.fill()
            ctx.stroke()
            
            // 3. Middle wider section - 7px height at y=52-59
            ctx.beginPath()
            ctx.roundRect(-14 * scale, 52 * scale, 28 * scale, 7 * scale, 3.5 * scale)
            ctx.fill()
            ctx.stroke()
            
            // 4. Hatched body section (1/3 of body) - 25px at y=59-84
            ctx.fillRect(-8 * scale, 59 * scale, 16 * scale, 25 * scale)
            ctx.strokeRect(-8 * scale, 59 * scale, 16 * scale, 25 * scale)
            
            // Hatch lines (3 lines in original)
            ctx.strokeStyle = "#000000"
            ctx.lineWidth = 0.5
            ctx.globalAlpha = 0.4
            ctx.beginPath()
            ctx.moveTo(-8 * scale, 65 * scale)
            ctx.lineTo(8 * scale, 65 * scale)
            ctx.moveTo(-8 * scale, 71 * scale)
            ctx.lineTo(8 * scale, 71 * scale)
            ctx.moveTo(-8 * scale, 77 * scale)
            ctx.lineTo(8 * scale, 77 * scale)
            ctx.stroke()
            ctx.globalAlpha = 1
            ctx.lineWidth = 1
            ctx.strokeStyle = "#000000"
            
            // 5. Solid body section (2/3 of body) - 60px at y=84-144
            ctx.fillStyle = bodyGradient
            ctx.beginPath()
            ctx.roundRect(-8 * scale, 84 * scale, 16 * scale, 60 * scale, 1 * scale)
            ctx.fill()
            ctx.stroke()
            
            // 6. Bottom cap (silver/gray) - 9px at y=144-153
            ctx.fillStyle = "#c0c0c0"
            ctx.strokeStyle = "#777777"
            ctx.beginPath()
            ctx.moveTo(-7 * scale, 144 * scale)
            ctx.lineTo(7 * scale, 144 * scale)
            ctx.lineTo(7 * scale, 150 * scale)
            ctx.quadraticCurveTo(7 * scale, 153 * scale, 4 * scale, 153 * scale)
            ctx.lineTo(-4 * scale, 153 * scale)
            ctx.quadraticCurveTo(-7 * scale, 153 * scale, -7 * scale, 150 * scale)
            ctx.closePath()
            ctx.fill()
            ctx.stroke()
            
            ctx.restore()
            break
          }

          case "measure": {
            ctx.setLineDash([])
            const mp0 = obj.points[0], mp1 = obj.points[1]
            ctx.beginPath(); ctx.moveTo(mp0.x, mp0.y); ctx.lineTo(mp1.x, mp1.y); ctx.stroke()
            const mAngle = Math.atan2(mp1.y - mp0.y, mp1.x - mp0.x)
            const aLen = 8
            for (const [px, py, flip] of [[mp0.x, mp0.y, 0], [mp1.x, mp1.y, Math.PI]] as [number, number, number][]) {
              ctx.beginPath(); ctx.moveTo(px, py)
              ctx.lineTo(px + Math.cos(mAngle + flip + 0.4) * aLen, py + Math.sin(mAngle + flip + 0.4) * aLen)
              ctx.moveTo(px, py)
              ctx.lineTo(px + Math.cos(mAngle + flip - 0.4) * aLen, py + Math.sin(mAngle + flip - 0.4) * aLen)
              ctx.stroke()
            }
            const mDist = Math.sqrt((mp1.x - mp0.x) ** 2 + (mp1.y - mp0.y) ** 2)
            ctx.font = "bold 10px monospace"
            ctx.fillStyle = obj.color
            ctx.textAlign = "center"; ctx.textBaseline = "bottom"
            ctx.fillText(`${Math.round(mDist)}px`, (mp0.x + mp1.x) / 2, (mp0.y + mp1.y) / 2 - 4)
            ctx.textAlign = "start"
            break
          }

        }

        // Reset shadow after drawing the main object shape
        ctx.shadowColor = "transparent"
        ctx.shadowBlur = 0

        // Draw comment bubble for objects with comment
        const hasCommentBox = !!obj.comment
        if (hasCommentBox) {
          ctx.setLineDash([])
          // Get unified comment box configuration for this object type
          const config = getCommentBoxConfig(obj)
          const offX = obj.commentOffset?.x ?? config.defaultOffX
          const offY = obj.commentOffset?.y ?? config.defaultOffY
          const rotation = (obj.commentRotation ?? 0) * Math.PI / 180
          const cfs = obj.fontSize ?? config.defaultFontSize
          const cff = obj.fontFamily ?? "monospace"
          const cbold = obj.fontBold ? "bold " : ""
          const citalic = obj.fontItalic ? "italic " : ""
          ctx.font = `${citalic}${cbold}${cfs}px ${cff}`
          const m = ctx.measureText(config.labelText)
          const boxW = m.width + config.padding * 2
          const boxH = cfs + 6
          // Use smart anchor or direct anchor based on object type
          const anchor = config.useSmartAnchor
            ? getCommentAnchor(obj, { x: config.anchorBase.x + offX, y: config.anchorBase.y + offY })
            : config.anchorBase
          // Box center position (before rotation)
          const boxCenterX = config.anchorBase.x + offX + m.width / 2
          const boxCenterY = config.anchorBase.y + offY
          // Box dimensions relative to center
          const halfW = boxW / 2
          const halfH = boxH / 2
          // Wire connection point (closest corner after rotation)
          const corners = [
            { lx: -halfW, ly: -halfH },
            { lx: halfW, ly: -halfH },
            { lx: -halfW, ly: halfH },
            { lx: halfW, ly: halfH },
          ]
          // Transform corners back to world coords to find closest
          let closestWorld = { x: 0, y: 0 }
          let minDist = Infinity
          for (const c of corners) {
            const wx = boxCenterX + c.lx * Math.cos(rotation) - c.ly * Math.sin(rotation)
            const wy = boxCenterY + c.lx * Math.sin(rotation) + c.ly * Math.cos(rotation)
            const d = Math.hypot(anchor.x - wx, anchor.y - wy)
            if (d < minDist) { minDist = d; closestWorld = { x: wx, y: wy } }
          }
          // Wire to closest corner (in world coords)
          ctx.strokeStyle = obj.color
          ctx.lineWidth = 0.8
          ctx.beginPath(); ctx.moveTo(anchor.x, anchor.y); ctx.lineTo(closestWorld.x, closestWorld.y); ctx.stroke()
          // Draw rotated box and text
          ctx.save()
          ctx.translate(boxCenterX, boxCenterY)
          ctx.rotate(rotation)
          // Box background
          ctx.fillStyle = "rgba(0,0,0,0.75)"
          ctx.beginPath(); ctx.roundRect(-halfW, -halfH, boxW, boxH, 2); ctx.fill()
          ctx.textBaseline = "middle"
          ctx.textAlign = "center"
          // Text with white stroke and object color fill
          ctx.strokeStyle = "#000"
          ctx.lineWidth = 0
          ctx.lineJoin = "round"
          ctx.strokeText(config.labelText, 0, 0)
          ctx.fillStyle = obj.color
          ctx.fillText(config.labelText, 0, 0)
          if (obj.fontUnderline) {
            ctx.strokeStyle = obj.color; ctx.lineWidth = 0.8
            ctx.beginPath(); ctx.moveTo(-m.width / 2, cfs / 2); ctx.lineTo(m.width / 2, cfs / 2); ctx.stroke()
          }
          ctx.restore()
          ctx.textAlign = "start"
          ctx.strokeStyle = obj.color
          ctx.lineWidth = obj.strokeWidth
        }
      }
      ctx.globalAlpha = 1
    }

    // Draw polyline/polygon in progress
    if ((store.activeTool === "polyline" || store.activeTool === "polygon") && polyPoints.length > 0) {
      ctx.setLineDash([])
      ctx.strokeStyle = store.activeColor
      ctx.lineWidth = store.strokeWidth
      ctx.lineCap = "round"; ctx.lineJoin = "round"
      applyLineDash(ctx, store.activeLineStyle, store.strokeWidth)
      ctx.beginPath()
      ctx.moveTo(polyPoints[0].x, polyPoints[0].y)
      for (let i = 1; i < polyPoints.length; i++) ctx.lineTo(polyPoints[i].x, polyPoints[i].y)
      if (polyPreview) ctx.lineTo(polyPreview.x, polyPreview.y)
      ctx.stroke()
      ctx.setLineDash([])
      for (const pt of polyPoints) {
        ctx.fillStyle = store.activeColor
        ctx.beginPath(); ctx.arc(pt.x, pt.y, 3, 0, Math.PI * 2); ctx.fill()
      }
    }

    // Draw in-progress two-click shape preview
    if (drawPhase === "first" && currentPoints.length >= 2) {
      ctx.setLineDash([4, 4])
      ctx.strokeStyle = store.activeColor
      ctx.lineWidth = store.strokeWidth
      ctx.lineCap = "round"; ctx.lineJoin = "round"
      switch (store.activeTool) {
        case "rect": {
          const [p0, p1] = currentPoints
          ctx.strokeRect(Math.min(p0.x, p1.x), Math.min(p0.y, p1.y), Math.abs(p1.x - p0.x), Math.abs(p1.y - p0.y))
          break
        }
        case "circle": {
          const c = currentPoints[0]
          const r = Math.sqrt((currentPoints[1].x - c.x) ** 2 + (currentPoints[1].y - c.y) ** 2)
          ctx.beginPath(); ctx.arc(c.x, c.y, r, 0, Math.PI * 2); ctx.stroke()
          break
        }
        case "measure": {
          ctx.beginPath()
          ctx.moveTo(currentPoints[0].x, currentPoints[0].y)
          ctx.lineTo(currentPoints[1].x, currentPoints[1].y)
          ctx.stroke()
          break
        }
      }
      ctx.setLineDash([])
    }

    // Draw in-progress freehand
    if (isDrawing && store.activeTool === "freehand" && currentPoints.length >= 2) {
      ctx.setLineDash([])
      ctx.strokeStyle = store.activeColor
      ctx.lineWidth = store.strokeWidth
      ctx.lineCap = "round"
      ctx.beginPath()
      ctx.moveTo(currentPoints[0].x, currentPoints[0].y)
      for (let i = 1; i < currentPoints.length; i++) ctx.lineTo(currentPoints[i].x, currentPoints[i].y)
      ctx.stroke()
    }

    // Selection handles
    const selObj = getSelectedObject()
    if (selObj) {
      ctx.setLineDash([])
      // Vertex handles (for probe, only show tail vertex - index 1)
      for (let i = 0; i < selObj.points.length; i++) {
        // Skip head vertex (index 0) for probe - only show tail for rotation
        if (selObj.type === "probe" && i === 0) continue
        const hp = selObj.points[i]
        ctx.fillStyle = "#fff"
        ctx.strokeStyle = selObj.color
        ctx.lineWidth = 1.5
        ctx.beginPath(); ctx.rect(hp.x - HANDLE_SIZE / 2, hp.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE); ctx.fill(); ctx.stroke()
      }

      // Midpoint handles for insertable vertices
      const canInsert = ["line", "polyline", "polygon"].includes(selObj.type)
      if (canInsert) {
        const pts = selObj.points
        const isClosed = selObj.type === "polygon"
        const count = isClosed ? pts.length : pts.length - 1
        for (let i = 0; i < count; i++) {
          const a = pts[i], b = pts[(i + 1) % pts.length]
          const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2
          ctx.fillStyle = "rgba(255,255,255,0.3)"
          ctx.strokeStyle = selObj.color
          ctx.lineWidth = 1
          ctx.beginPath(); ctx.arc(mx, my, MID_HANDLE_SIZE / 2, 0, Math.PI * 2); ctx.fill(); ctx.stroke()
        }
      }

      // Comment drag handle and rotation handle for all comment boxes
      const selHasCommentBox = !!selObj.comment
      if (selHasCommentBox) {
        // Use unified comment box configuration
        const config = getCommentBoxConfig(selObj)
        const offX = selObj.commentOffset?.x ?? config.defaultOffX
        const offY = selObj.commentOffset?.y ?? config.defaultOffY
        const rotation = (selObj.commentRotation ?? 0) * Math.PI / 180
        const cfs = selObj.fontSize ?? config.defaultFontSize
        const cff = selObj.fontFamily ?? "monospace"
        const cbold = selObj.fontBold ? "bold " : ""
        const citalic = selObj.fontItalic ? "italic " : ""
        ctx.font = `${citalic}${cbold}${cfs}px ${cff}`
        const m = ctx.measureText(config.labelText)
        const boxW = m.width + config.padding * 2
        const boxH = cfs + 6
        const halfW = boxW / 2
        const halfH = boxH / 2
        const boxCenterX = config.anchorBase.x + offX + m.width / 2
        const boxCenterY = config.anchorBase.y + offY
        // Drag handle at center
        ctx.fillStyle = "rgba(255,255,255,0.5)"
        ctx.strokeStyle = selObj.color
        ctx.lineWidth = 1
        ctx.beginPath(); ctx.arc(boxCenterX, boxCenterY, 4, 0, Math.PI * 2); ctx.fill(); ctx.stroke()
        // Rotation handle at top-right corner (in rotated space)
        const rotHandleLocalX = halfW + 8
        const rotHandleLocalY = -halfH - 8
        const rotHandleWorldX = boxCenterX + rotHandleLocalX * Math.cos(rotation) - rotHandleLocalY * Math.sin(rotation)
        const rotHandleWorldY = boxCenterY + rotHandleLocalX * Math.sin(rotation) + rotHandleLocalY * Math.cos(rotation)
        // Draw line from corner to rotation handle
        const cornerX = boxCenterX + halfW * Math.cos(rotation) - (-halfH) * Math.sin(rotation)
        const cornerY = boxCenterY + halfW * Math.sin(rotation) + (-halfH) * Math.cos(rotation)
        ctx.strokeStyle = selObj.color
        ctx.lineWidth = 1
        ctx.setLineDash([2, 2])
        ctx.beginPath(); ctx.moveTo(cornerX, cornerY); ctx.lineTo(rotHandleWorldX, rotHandleWorldY); ctx.stroke()
        ctx.setLineDash([])
        // Rotation handle (circular with rotation icon)
        ctx.fillStyle = "#4ade80"
        ctx.strokeStyle = "#166534"
        ctx.lineWidth = 1.5
        ctx.beginPath(); ctx.arc(rotHandleWorldX, rotHandleWorldY, 6, 0, Math.PI * 2); ctx.fill(); ctx.stroke()
        // Draw rotation arrow icon
        ctx.strokeStyle = "#166534"
        ctx.lineWidth = 1.5
        ctx.beginPath()
        ctx.arc(rotHandleWorldX, rotHandleWorldY, 3, -Math.PI * 0.7, Math.PI * 0.5)
        ctx.stroke()
        // Arrow head
        const arrowAngle = Math.PI * 0.5
        ctx.beginPath()
        ctx.moveTo(rotHandleWorldX + 3 * Math.cos(arrowAngle), rotHandleWorldY + 3 * Math.sin(arrowAngle))
        ctx.lineTo(rotHandleWorldX + 3 * Math.cos(arrowAngle) + 3, rotHandleWorldY + 3 * Math.sin(arrowAngle) - 1)
        ctx.stroke()
      }

    }

    ctx.restore()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    store.layers, store.panOffset, store.zoom, store.activeTool,
    store.activeColor, store.strokeWidth, store.activeLineStyle,
    store.imageGrayscale, store.imageTint, store.imageOverlay, store.imageOverlayOpacity,
    imageLoaded, isDrawing, currentPoints,
    polyPoints, polyPreview, selectedObjectId,
    selectedLayerId, getSelectedObject, drawPhase,
    selectedNet, blinkingNet, blinkVisible,
  ])

  useEffect(() => {
    const frame = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(frame)
  }, [draw])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver(() => draw())
    observer.observe(container)
    return () => observer.disconnect()
  }, [draw])

  // --- MOUSE HANDLERS ---
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const point = screenToCanvas(e.clientX, e.clientY)
      setMouseCanvas(point)

      // Right mouse button = pan (item 4)
      if (e.button === 2 || e.button === 1) {
        // If drawing polyline/polygon, right-click finishes it (item 3)
        const tool = store.activeTool
        const layer = store.activeLayer
        if ((tool === "polyline" || tool === "polygon") && polyPoints.length > 0 && layer) {
          if (tool === "polyline" && polyPoints.length >= 2) {
            store.addObjectToLayer(layer.id, {
              type: "polyline", points: polyPoints,
              color: store.activeColor, strokeWidth: store.strokeWidth,
              lineStyle: store.activeLineStyle,
            })
            setConsoleMsg(`Polyline created with ${polyPoints.length} points.`)
          } else if (tool === "polygon" && polyPoints.length >= 3) {
            store.addObjectToLayer(layer.id, {
              type: "polygon", points: polyPoints,
              color: store.activeColor, strokeWidth: store.strokeWidth,
              closed: true, lineStyle: store.activeLineStyle,
            })
            setConsoleMsg(`Polygon created with ${polyPoints.length} points.`)
          }
          setPolyPoints([])
          setPolyPreview(null)
          return
        }
        // Otherwise pan
        setIsPanning(true)
        setPanStart({ x: e.clientX - store.panOffset.x, y: e.clientY - store.panOffset.y })
        return
      }

      // Alt-drag also pans
      if (e.altKey) {
        setIsPanning(true)
        setPanStart({ x: e.clientX - store.panOffset.x, y: e.clientY - store.panOffset.y })
        return
      }

      const tool = store.activeTool
      const layer = store.activeLayer
      if (!layer || layer.locked || !layer.visible) {
        if (tool === "select") { setSelectedObjectId(null); setSelectedLayerId(null) }
        return
      }

      // --- SELECT TOOL ---
      if (tool === "select") {
        const selObj = getSelectedObject()

        // Helper to get rotation handle position for comment boxes
        const getCommentRotationHandle = (obj: DrawObject): { handlePos: Point, centerPos: Point } | null => {
          // Use unified comment box configuration
          const config = getCommentBoxConfig(obj)
          // Check if object has comment
          if (!config.labelText) return null
          const offX = obj.commentOffset?.x ?? config.defaultOffX
          const offY = obj.commentOffset?.y ?? config.defaultOffY
          const cfs = obj.fontSize ?? config.defaultFontSize
          const rotation = (obj.commentRotation ?? 0) * Math.PI / 180
          const cff = obj.fontFamily ?? "monospace"
          const cbold = obj.fontBold ? "bold " : ""
          const citalic = obj.fontItalic ? "italic " : ""
          const canvas = canvasRef.current
          if (!canvas) return null
          const ctx = canvas.getContext("2d")
          if (!ctx) return null
          ctx.font = `${citalic}${cbold}${cfs}px ${cff}`
          const m = ctx.measureText(config.labelText)
          const boxW = m.width + config.padding * 2
          const boxH = cfs + 6
          const halfW = boxW / 2
          const halfH = boxH / 2
          const boxCenterX = config.anchorBase.x + offX + m.width / 2
          const boxCenterY = config.anchorBase.y + offY
          const rotHandleLocalX = halfW + 8
          const rotHandleLocalY = -halfH - 8
          const rotHandleWorldX = boxCenterX + rotHandleLocalX * Math.cos(rotation) - rotHandleLocalY * Math.sin(rotation)
          const rotHandleWorldY = boxCenterY + rotHandleLocalX * Math.sin(rotation) + rotHandleLocalY * Math.cos(rotation)
          return { handlePos: { x: rotHandleWorldX, y: rotHandleWorldY }, centerPos: { x: boxCenterX, y: boxCenterY } }
        }

        // Check comment rotation handle
        if (selObj && selObj.comment) {
          const rotHandle = getCommentRotationHandle(selObj)
          if (rotHandle && dist(point, rotHandle.handlePos) < HANDLE_HIT / store.zoom) {
            store.pushUndo()
            setIsDraggingCommentRotation(true)
            setCommentRotationCenter(rotHandle.centerPos)
            return
          }
        }

        // Check comment drag handle
        if (selObj && selObj.comment) {
          const rotHandle = getCommentRotationHandle(selObj)
          if (rotHandle && dist(point, rotHandle.centerPos) < HANDLE_HIT / store.zoom) {
            setIsDraggingComment(true); setCommentDragStart(point); return
          }
        }

// Check vertex handle drag (for probe, only tail vertex index 1 is draggable)
      if (selObj) {
      for (let i = 0; i < selObj.points.length; i++) {
      // Skip head vertex (index 0) for probe - head is not draggable as a handle
      if (selObj.type === "probe" && i === 0) continue
      if (dist(point, selObj.points[i]) < HANDLE_HIT / store.zoom) {
      store.pushUndo()
      setDragHandle({ layerId: selObj.layerId, objectId: selObj.id, pointIndex: i })
      return
      }
      }
          // Check midpoint handles for insert
          const canInsert = ["line", "polyline", "polygon"].includes(selObj.type)
          if (canInsert) {
            const pts = selObj.points
            const isClosed = selObj.type === "polygon"
            const count = isClosed ? pts.length : pts.length - 1
            for (let i = 0; i < count; i++) {
              const a = pts[i], b = pts[(i + 1) % pts.length]
              const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2
              if (dist(point, { x: mx, y: my }) < MID_HANDLE_SIZE / store.zoom) {
                const newPoints = [...selObj.points]
                newPoints.splice(i + 1, 0, { x: mx, y: my })
                store.updateObjectWithUndo(selObj.layerId, selObj.id, { points: newPoints })
                setDragHandle({ layerId: selObj.layerId, objectId: selObj.id, pointIndex: i + 1 })
                setConsoleMsg("Vertex inserted. Drag to position.")
                return
              }
            }
          }
        }

        // Hit test - for text allow dragging object
        const threshold = 8 / store.zoom
        let found = false
        for (let li = store.layers.length - 1; li >= 0; li--) {
          const l = store.layers[li]
          if (!l.visible) continue
          for (let oi = l.objects.length - 1; oi >= 0; oi--) {
            // Skip hidden objects from selection
            if (l.objects[oi].visible === false) continue
            if (hitTestObject(l.objects[oi], point, threshold)) {
setSelectedObjectId(l.objects[oi].id)
                  setSelectedLayerId(l.id)
                  // Start object drag for text and probe (drag from anywhere on body)
                  const hitObj = l.objects[oi]
                  if (hitObj.type === "text" || hitObj.type === "probe") {
                    store.pushUndo()
                    setIsDraggingObject(true)
                    setObjectDragStart(point)
                  }
                  found = true
              break
            }
          }
          if (found) break
        }
        if (!found) {
          setSelectedObjectId(null); setSelectedLayerId(null)
          // Left-click on empty space starts panning
          setIsPanning(true)
          setPanStart({ x: e.clientX - store.panOffset.x, y: e.clientY - store.panOffset.y })
        }
        return
      }

      // --- ERASER ---
      if (tool === "eraser") {
        const threshold = 8 / store.zoom
        for (let li = store.layers.length - 1; li >= 0; li--) {
          const l = store.layers[li]
          if (!l.visible || l.locked) continue
          for (let oi = l.objects.length - 1; oi >= 0; oi--) {
            // Skip hidden objects from eraser
            if (l.objects[oi].visible === false) continue
            if (hitTestObject(l.objects[oi], point, threshold)) {
              store.removeObject(l.id, l.objects[oi].id)
              setConsoleMsg("Object erased.")
              return
            }
          }
        }
        return
      }

      // --- TEXT TOOL ---
      if (tool === "text") {
        const canvasEl = canvasRef.current
        if (!canvasEl) return
        const rect = canvasEl.getBoundingClientRect()
        setTextInput({ x: e.clientX - rect.left, y: e.clientY - rect.top, visible: true, mode: "text" })
        setTextValue("")
        setTimeout(() => textInputRef.current?.focus(), 50)
        return
      }

      // --- MARKER TOOL ---
      if (tool === "marker") {
        store.addObjectToLayer(layer.id, {
          type: "marker",
          points: [point],
          color: store.activeColor,
          strokeWidth: 1,
          markerSize: 12,
          markerSymbol: "circle",
          markerFilled: true,
        })
        setConsoleMsg("Marker placed.")
        return
      }

      // --- PROBE TOOL ---
      if (tool === "probe") {
        // Create probe with head at click position, tail above head
        const probeSize = 90 // Default size
        // Tail should be above head, rotated 27 degrees clockwise from vertical up
        // Vertical up from head is -90deg, then rotate 27deg clockwise (add 27)
        // But tail is ABOVE head, so we add to head position going UP
        const angle = (90 - 27) * (Math.PI / 180) // 63 degrees from horizontal = pointing up-left
        // probeSize IS the total visual length (153 in original SVG coords, scaled)
        // Tail position relative to head (tail is above)
        const tailX = point.x - Math.cos(angle) * probeSize
        const tailY = point.y - Math.sin(angle) * probeSize
        store.addObjectToLayer(layer.id, {
          type: "probe",
          points: [point, { x: tailX, y: tailY }], // head at tip, tail at cap end
          color: store.activeColor,
          strokeWidth: 1,
          probeSize: probeSize,
        })
        setConsoleMsg("Probe placed. Drag tail vertex to rotate, drag body to move.")
        return
      }

      // --- POLYLINE / POLYGON ---
      if (tool === "polyline" || tool === "polygon") {
        if (polyPoints.length === 0) { setPolyPoints([point]) }
        else {
          const snapped = snapAngle(polyPoints[polyPoints.length - 1], point)
          setPolyPoints((prev) => [...prev, snapped])
        }
        return
      }

      // --- RECT, CIRCLE, MEASURE: two-click mode (item 14) ---
      if (tool === "rect" || tool === "circle" || tool === "measure") {
        if (drawPhase === "idle") {
          setDrawPhase("first")
          setCurrentPoints([point, point])
          setConsoleMsg("First point set. Click to set second point.")
        } else {
          // Second click - create object
          store.addObjectToLayer(layer.id, {
            type: tool, points: [currentPoints[0], point],
            color: store.activeColor, strokeWidth: store.strokeWidth,
            lineStyle: store.activeLineStyle,
          })
          setDrawPhase("idle")
          setCurrentPoints([])
          setConsoleMsg(`${tool.charAt(0).toUpperCase() + tool.slice(1)} created.`)
        }
        return
      }

      // --- FREEHAND ---
      if (tool === "freehand") {
        setIsDrawing(true)
        setCurrentPoints([point, point])
      }
    },
    [screenToCanvas, store, polyPoints, snapAngle, getSelectedObject, drawPhase, currentPoints]
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const point = screenToCanvas(e.clientX, e.clientY)
      setMouseCanvas(point)

      if (isPanning) {
        store.setPanOffset({ x: e.clientX - panStart.x, y: e.clientY - panStart.y })
        return
      }

      // Dragging comment rotation handle
      if (isDraggingCommentRotation && commentRotationCenter) {
        const selObj = getSelectedObject()
        if (selObj) {
          // Calculate angle from box center to mouse
          const angle = Math.atan2(point.y - commentRotationCenter.y, point.x - commentRotationCenter.x)
          // Convert to degrees and offset to make top-right corner the 0 position
          let deg = (angle * 180 / Math.PI) + 45
          // Normalize to -180 to 180 range
          deg = ((deg + 180) % 360) - 180
          // Snap to 15 degree increments if shift is held
          if (shiftHeld) {
            deg = Math.round(deg / 15) * 15
          }
          store.updateObject(selObj.layerId, selObj.id, { commentRotation: Math.round(deg) })
        }
        return
      }

      // Dragging a comment offset
      if (isDraggingComment && commentDragStart) {
        const selObj = getSelectedObject()
        if (selObj) {
          // Use unified comment box configuration for defaults
          const config = getCommentBoxConfig(selObj)
          const prevOffX = selObj.commentOffset?.x ?? config.defaultOffX
          const prevOffY = selObj.commentOffset?.y ?? config.defaultOffY
          const dx = point.x - commentDragStart.x
          const dy = point.y - commentDragStart.y
          store.updateObject(selObj.layerId, selObj.id, { commentOffset: { x: prevOffX + dx, y: prevOffY + dy } })
          setCommentDragStart(point)
        }
        return
      }

      // Dragging an object (text)
      if (isDraggingObject && objectDragStart) {
        const selObj = getSelectedObject()
        if (selObj) {
          const dx = point.x - objectDragStart.x
          const dy = point.y - objectDragStart.y
          const newPoints = selObj.points.map((p) => ({ x: p.x + dx, y: p.y + dy }))
          store.updateObject(selObj.layerId, selObj.id, { points: newPoints })
          setObjectDragStart(point)
        }
        return
      }

      // Handle drag
      if (dragHandle) {
        const { layerId, objectId, pointIndex } = dragHandle
        const layer = store.layers.find((l) => l.id === layerId)
        const obj = layer?.objects.find((o) => o.id === objectId)
        if (obj) {
          const newPoints = [...obj.points]
          // Circle: moving center (index 0) keeps radius
          if (obj.type === "circle" && pointIndex === 0) {
            const dx = point.x - obj.points[0].x
            const dy = point.y - obj.points[0].y
            newPoints[0] = point
            newPoints[1] = { x: obj.points[1].x + dx, y: obj.points[1].y + dy }
          } else if (obj.type === "probe") {
            // Probe: head (0) moves body, tail (1) rotates around head
            if (pointIndex === 0) {
              // Move both head and tail to maintain angle
              const dx = point.x - obj.points[0].x
              const dy = point.y - obj.points[0].y
              newPoints[0] = point
              newPoints[1] = { x: obj.points[1].x + dx, y: obj.points[1].y + dy }
            } else {
              // Rotate tail around head (keep same distance)
              const head = obj.points[0]
              const currentDist = Math.hypot(obj.points[1].x - head.x, obj.points[1].y - head.y)
              const newAngle = Math.atan2(point.y - head.y, point.x - head.x)
              newPoints[1] = {
                x: head.x + Math.cos(newAngle) * currentDist,
                y: head.y + Math.sin(newAngle) * currentDist,
              }
            }
          } else {
            newPoints[pointIndex] = point
          }
          store.updateObject(layerId, objectId, { points: newPoints })
        }
        return
      }

      // Polyline/polygon preview
      if ((store.activeTool === "polyline" || store.activeTool === "polygon") && polyPoints.length > 0) {
        const snapped = snapAngle(polyPoints[polyPoints.length - 1], point)
        setPolyPreview(snapped)
        return
      }

      // Two-click shape preview
      if (drawPhase === "first" && (store.activeTool === "rect" || store.activeTool === "circle" || store.activeTool === "measure")) {
        setCurrentPoints((prev) => [prev[0], point])
        return
      }

      // Freehand drawing
      if (!isDrawing) return
      if (store.activeTool === "freehand") {
        setCurrentPoints((prev) => [...prev, point])
      }
    },
    [screenToCanvas, isPanning, panStart, isDrawing, dragHandle, isDraggingComment, commentDragStart, isDraggingObject, objectDragStart, isDraggingCommentRotation, commentRotationCenter, shiftHeld, store, polyPoints, snapAngle, getSelectedObject, drawPhase]
  )

  const handleMouseUp = useCallback(() => {
    if (isPanning) { setIsPanning(false); return }
    if (isDraggingCommentRotation) { setIsDraggingCommentRotation(false); setCommentRotationCenter(null); return }
    if (isDraggingComment) { setIsDraggingComment(false); setCommentDragStart(null); return }
    if (isDraggingObject) { setIsDraggingObject(false); setObjectDragStart(null); return }
    if (dragHandle) { setDragHandle(null); return }

    if (!isDrawing) return
    const layer = store.activeLayer
    if (!layer || currentPoints.length < 2) { setIsDrawing(false); setCurrentPoints([]); return }

    // Freehand finalize
    if (store.activeTool === "freehand") {
      store.addObjectToLayer(layer.id, {
        type: "freehand", points: currentPoints,
        color: store.activeColor, strokeWidth: store.strokeWidth,
        lineStyle: store.activeLineStyle,
      })
      setConsoleMsg("Freehand stroke created.")
    }
    setIsDrawing(false)
    setCurrentPoints([])
  }, [isPanning, isDraggingCommentRotation, isDraggingComment, isDraggingObject, dragHandle, isDrawing, store, currentPoints])

  // --- Double-click handler ---
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      const point = screenToCanvas(e.clientX, e.clientY)
      const tool = store.activeTool

      if (tool === "select" && selectedObjectId && selectedLayerId) {
        const selObj = getSelectedObject()

        // Double-click on vertex to remove it
        if (selObj && ["line", "polyline", "polygon"].includes(selObj.type)) {
          const threshold = HANDLE_HIT / store.zoom
          for (let i = 0; i < selObj.points.length; i++) {
            if (dist(point, selObj.points[i]) < threshold) {
              const minPts = selObj.type === "line" ? 2 : selObj.type === "polygon" ? 3 : 2
              if (selObj.points.length <= minPts) { setConsoleMsg(`Cannot remove: minimum ${minPts} vertices.`); return }
              const newPts = selObj.points.filter((_, idx) => idx !== i)
              store.updateObjectWithUndo(selObj.layerId, selObj.id, { points: newPts })
              setConsoleMsg("Vertex removed.")
              return
            }
          }
        }

        // Double-click on text object -> edit its content (item 6)
        if (selObj && selObj.type === "text") {
          const canvasEl = canvasRef.current
          if (!canvasEl) return
          const rect = canvasEl.getBoundingClientRect()
          setTextInput({
            x: e.clientX - rect.left, y: e.clientY - rect.top, visible: true,
            mode: "editText", layerId: selObj.layerId, objectId: selObj.id,
          })
          setTextValue(selObj.text ?? "")
          setTimeout(() => textInputRef.current?.focus(), 50)
          return
        }

        // Double-click on any other object -> open edit dialog with Comment and Net fields
        const threshold = 8 / store.zoom
        for (let li = store.layers.length - 1; li >= 0; li--) {
          const l = store.layers[li]
          if (!l.visible) continue
          for (let oi = l.objects.length - 1; oi >= 0; oi--) {
            const obj = l.objects[oi]
            if (obj.type === "text") continue // text objects cannot have comments/nets
            if (obj.visible === false) continue // skip hidden objects
            if (hitTestObject(obj, point, threshold)) {
              const canvasEl = canvasRef.current
              if (!canvasEl) return
              const rect = canvasEl.getBoundingClientRect()
              setObjectEditDialog({
                visible: true,
                x: e.clientX - rect.left, y: e.clientY - rect.top,
                layerId: l.id, objectId: obj.id,
                comment: obj.comment ?? "",
                net: obj.net ?? "",
              })
              setConsoleMsg("Editing object properties.")
              return
            }
          }
        }
        return
      }
    },
    [screenToCanvas, store, selectedObjectId, selectedLayerId, getSelectedObject]
  )

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault()
      // Ctrl+scroll = zoom (centered on cursor)
      if (e.ctrlKey || e.metaKey) {
        const factor = e.deltaY > 0 ? 0.9 : 1.1
        const newZoom = Math.max(0.1, Math.min(10, store.zoom * factor))
        const canvas = canvasRef.current
        if (!canvas) { store.setZoom(newZoom); return }
        const rect = canvas.getBoundingClientRect()
        const mx = e.clientX - rect.left, my = e.clientY - rect.top
        const wx = (mx - store.panOffset.x) / store.zoom
        const wy = (my - store.panOffset.y) / store.zoom
        store.setZoom(newZoom)
        store.setPanOffset({ x: mx - wx * newZoom, y: my - wy * newZoom })
      } else if (e.shiftKey) {
        // Shift+scroll = horizontal pan
        store.setPanOffset({ x: store.panOffset.x - e.deltaY, y: store.panOffset.y })
      } else {
        // Normal scroll = vertical pan
        store.setPanOffset({ x: store.panOffset.x, y: store.panOffset.y - e.deltaY })
      }
    },
    [store]
  )

  // --- TEXT INPUT SUBMIT ---
  const handleTextSubmit = useCallback(() => {
    if (!textInput.visible) { return }

    if (textInput.mode === "editComment" && textInput.layerId && textInput.objectId) {
      store.setObjectComment(textInput.layerId, textInput.objectId, textValue)
      setTextInput((prev) => ({ ...prev, visible: false }))
      setTextValue("")
      setConsoleMsg("Comment updated.")
      return
    }

    if (textInput.mode === "editPointObj" && textInput.layerId && textInput.objectId) {
      store.updateObjectWithUndo(textInput.layerId, textInput.objectId, { text: textValue })
      setTextInput((prev) => ({ ...prev, visible: false }))
      setTextValue("")
      setConsoleMsg("Comment text updated.")
      return
    }

    if (textInput.mode === "editText" && textInput.layerId && textInput.objectId) {
      store.updateObjectWithUndo(textInput.layerId, textInput.objectId, { text: textValue })
      setTextInput((prev) => ({ ...prev, visible: false }))
      setTextValue("")
      setConsoleMsg("Text updated.")
      return
    }

    if (!textValue.trim()) {
      setTextInput((prev) => ({ ...prev, visible: false }))
      return
    }

    const layer = store.activeLayer
    if (!layer) return
    const canvasEl = canvasRef.current
    if (!canvasEl) return
    const rect = canvasEl.getBoundingClientRect()
    const point = screenToCanvas(rect.left + textInput.x, rect.top + textInput.y)

    if (textInput.mode === "text") {
      store.addObjectToLayer(layer.id, { type: "text", points: [point], color: store.activeColor, strokeWidth: store.strokeWidth, text: textValue })
      setConsoleMsg("Text placed.")
    }

    setTextInput((prev) => ({ ...prev, visible: false }))
    setTextValue("")
  }, [textInput, textValue, store, screenToCanvas])

  // Delete selected object, undo/redo shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if ((e.key === "Delete" || e.key === "Backspace") && selectedObjectId && selectedLayerId) {
        store.removeObject(selectedLayerId, selectedObjectId)
        setSelectedObjectId(null); setSelectedLayerId(null)
        setConsoleMsg("Object deleted.")
      }
      if (e.key === "Escape") {
        if (polyPoints.length > 0) { setPolyPoints([]); setPolyPreview(null); setConsoleMsg("Drawing cancelled.") }
        else if (drawPhase !== "idle") { setDrawPhase("idle"); setCurrentPoints([]); setConsoleMsg("Drawing cancelled.") }
        else { store.setActiveTool("select"); setConsoleMsg("Select mode.") } // item 14
      }
      // Ctrl+Z / Ctrl+Y
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) { e.preventDefault(); store.undo(); setConsoleMsg("Undo.") }
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.key === "z" && e.shiftKey))) { e.preventDefault(); store.redo(); setConsoleMsg("Redo.") }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [selectedObjectId, selectedLayerId, store, polyPoints, drawPhase])

  // Cursor - use crosshair for all drawing tools including text, comment (Point), polyline
  const getCursor = () => {
    if (isPanning) return "grabbing"
    if (isDraggingCommentRotation) return "grabbing"
    switch (store.activeTool) {
      case "select": return "default"
      default: return "crosshair"
    }
  }

  const selObj = getSelectedObject()

  return (
    <div ref={containerRef} id="pcb-canvas-container" className="relative flex-1 overflow-hidden bg-[#1a1a2e]">
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{ cursor: getCursor() }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        onWheel={handleWheel}
        onContextMenu={(e) => e.preventDefault()}
      />

      {/* Console message (top-left, single line, highlighted border) */}
      <div className="absolute top-2 left-2 z-10 rounded border border-primary/60 bg-secondary/90 px-3 py-1 shadow backdrop-blur-sm pointer-events-none">
        <span className="text-[10px] font-mono text-foreground/80 leading-none whitespace-nowrap">{consoleMsg}</span>
      </div>

      {/* Text inline input */}
      {textInput.visible && (
        <div className="absolute z-20" style={{ left: textInput.x, top: textInput.y }}>
          <input
            ref={textInputRef}
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleTextSubmit()
              if (e.key === "Escape") setTextInput((prev) => ({ ...prev, visible: false }))
            }}
            onBlur={handleTextSubmit}
            placeholder={
              textInput.mode === "text" ? "Type text..." :
                textInput.mode === "editComment" ? "Edit comment..." :
                  textInput.mode === "editText" ? "Edit text..." :
                    "Add comment..."
            }
            className="rounded border border-border bg-popover px-2 py-1 text-xs text-foreground outline-none shadow-lg min-w-32"
          />
        </div>
      )}

      {/* Object Edit Dialog (double-click) - Comment and Net fields */}
      {objectEditDialog && objectEditDialog.visible && (
        <div
          className="absolute z-30 rounded-lg border border-border bg-popover/95 p-3 shadow-xl backdrop-blur-sm min-w-56"
          style={{ left: Math.min(objectEditDialog.x, window.innerWidth - 250), top: objectEditDialog.y }}
        >
          <div className="space-y-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Edit Object
            </div>

            {/* Comment field */}
            <div className="space-y-1">
              <label className="text-[10px] text-muted-foreground">Comment</label>
              <input
                type="text"
                value={objectEditDialog.comment}
                onChange={(e) => setObjectEditDialog(prev => prev ? { ...prev, comment: e.target.value } : null)}
                placeholder="Add comment..."
                className="w-full rounded border border-border bg-secondary px-2 py-1.5 text-xs text-foreground outline-none focus:border-primary"
                autoFocus
              />
            </div>

            {/* Net field */}
            <div className="space-y-1">
              <label className="text-[10px] text-muted-foreground">Net</label>
              <input
                type="text"
                value={objectEditDialog.net}
                onChange={(e) => setObjectEditDialog(prev => prev ? { ...prev, net: e.target.value } : null)}
                placeholder="e.g. VCC, GND, NET1..."
                className="w-full rounded border border-border bg-secondary px-2 py-1.5 text-xs text-foreground outline-none focus:border-primary"
                list="net-suggestions"
              />
              {/* Datalist for net suggestions */}
              <datalist id="net-suggestions">
                {Array.from(new Set(
                  store.layers.flatMap(l => l.objects.map(o => o.net).filter(Boolean))
                )).map(net => (
                  <option key={net} value={net} />
                ))}
              </datalist>
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={() => setObjectEditDialog(null)}
                className="rounded px-3 py-1 text-[10px] text-muted-foreground hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (objectEditDialog) {
                    store.updateObjectWithUndo(objectEditDialog.layerId, objectEditDialog.objectId, {
                      comment: objectEditDialog.comment || undefined,
                      net: objectEditDialog.net || undefined,
                    })
                    setObjectEditDialog(null)
                    setConsoleMsg("Object updated.")
                  }
                }}
                className="rounded bg-primary px-3 py-1 text-[10px] text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Object properties panel (edit mode) */}
      {selObj && store.activeTool === "select" && (
        <div className="absolute top-10 left-2 z-10 rounded-lg border border-border bg-popover/95 p-3 shadow-lg backdrop-blur-sm min-w-48 max-h-[80vh] overflow-y-auto">
          <div className="space-y-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {selObj.type} Properties
            </div>

            {/* Color */}
            <div className="space-y-1">
              <span className="text-[10px] text-muted-foreground">Color</span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {PRESET_COLORS.slice(0, 8).map((c) => (
                  <button
                    key={c}
                    onClick={() => store.updateObject(selObj.layerId, selObj.id, { color: c })}
                    className={`h-5 w-5 rounded-sm ring-1 transition-all ${selObj.color === c ? "ring-foreground scale-110" : "ring-border hover:ring-muted-foreground"
                      }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
                <input
                  type="color"
                  value={selObj.color}
                  onChange={(e) => store.updateObject(selObj.layerId, selObj.id, { color: e.target.value })}
                  className="h-5 w-5 cursor-pointer rounded border-0 bg-transparent p-0"
                />
              </div>
            </div>

            {/* Stroke Width */}
            {selObj.type !== "text" && selObj.type !== "marker" && selObj.type !== "probe" && (
              <div className="space-y-1">
                <span className="text-[10px] text-muted-foreground">Width: {selObj.strokeWidth}</span>
                <Slider
                  min={1} max={10} step={1}
                  value={[selObj.strokeWidth]}
                  onValueChange={([v]) => store.updateObject(selObj.layerId, selObj.id, { strokeWidth: v })}
                  className="w-full"
                />
              </div>
            )}

            {/* Line Style */}
            {["line", "polyline", "polygon", "rect", "circle", "freehand", "measure"].includes(selObj.type) && (
              <div className="space-y-1">
                <span className="text-[10px] text-muted-foreground">Style</span>
                <div className="flex items-center gap-1">
                  {(["solid", "dotted", "dashed", "dashdot"] as LineStyle[]).map((ls) => (
                    <button
                      key={ls}
                      onClick={() => store.updateObject(selObj.layerId, selObj.id, { lineStyle: ls })}
                      className={`rounded px-1.5 py-1 transition-colors ${(selObj.lineStyle ?? "solid") === ls
                        ? "bg-tool-active text-primary-foreground"
                        : "text-muted-foreground hover:bg-tool-hover"
                        }`}
                    >
                      <svg width="20" height="8" viewBox="0 0 20 8">
                        <line x1="0" y1="4" x2="20" y2="4" stroke="currentColor" strokeWidth="2"
                          strokeDasharray={ls === "solid" ? "none" : ls === "dotted" ? "2 3" : ls === "dashed" ? "5 3" : "5 2 2 2"}
                        />
                      </svg>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Border Style (simple/double line) */}
            {["polyline", "polygon", "rect", "circle", "freehand"].includes(selObj.type) && (
              <div className="space-y-1">
                <span className="text-[10px] text-muted-foreground">Border Style</span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => store.updateObject(selObj.layerId, selObj.id, { lineBodyStyle: "simple" })}
                    className={`rounded px-2 py-1 transition-colors ${(selObj.lineBodyStyle ?? "simple") === "simple" ? "bg-tool-active text-primary-foreground" : "text-muted-foreground hover:bg-tool-hover"}`}
                    title="Simple line"
                  >
                    <svg width="20" height="10" viewBox="0 0 20 10"><line x1="0" y1="5" x2="20" y2="5" stroke="currentColor" strokeWidth="2" /></svg>
                  </button>
                  <button
                    onClick={() => store.updateObject(selObj.layerId, selObj.id, { lineBodyStyle: "double" })}
                    className={`rounded px-2 py-1 transition-colors ${selObj.lineBodyStyle === "double" ? "bg-tool-active text-primary-foreground" : "text-muted-foreground hover:bg-tool-hover"}`}
                    title="Double line"
                  >
                    <svg width="20" height="10" viewBox="0 0 20 10"><line x1="0" y1="3" x2="20" y2="3" stroke="currentColor" strokeWidth="1.5" /><line x1="0" y1="7" x2="20" y2="7" stroke="currentColor" strokeWidth="1.5" /></svg>
                  </button>
                </div>
              </div>
            )}

            {/* Fill options for closed shapes */}
            {["polygon", "rect", "circle"].includes(selObj.type) && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selObj.filled ?? false}
                      onChange={(e) => store.updateObject(selObj.layerId, selObj.id, { filled: e.target.checked, fillOpacity: selObj.fillOpacity ?? 50 })}
                      className="rounded"
                    />
                    Fill
                  </label>
                  {selObj.filled && (
                    <div className="flex-1 flex items-center gap-1">
                      <Slider
                        min={0} max={100} step={5}
                        value={[selObj.fillOpacity ?? 50]}
                        onValueChange={([v]) => store.updateObject(selObj.layerId, selObj.id, { fillOpacity: v })}
                        className="flex-1"
                      />
                      <span className="text-[9px] font-mono text-muted-foreground w-7 text-right">{selObj.fillOpacity ?? 50}%</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Marker-specific properties */}
            {selObj.type === "marker" && (
              <>
                {/* Marker Size */}
                <div className="space-y-1">
                  <span className="text-[10px] text-muted-foreground">Size: {selObj.markerSize ?? 12}px</span>
                  <Slider
                    min={4} max={20} step={1}
                    value={[selObj.markerSize ?? 12]}
                    onValueChange={([v]) => store.updateObject(selObj.layerId, selObj.id, { markerSize: v })}
                    className="w-full"
                  />
                </div>

                {/* Marker Symbol - Filled and Not-Filled side by side */}
                <div className="space-y-1">
                  <span className="text-[10px] text-muted-foreground">Symbol</span>
                  <div className="grid grid-cols-6 gap-1">
                    {(["circle", "rectangle", "diamond"] as MarkerSymbol[]).map((symbol) => {
                      const currentSymbol = selObj.markerSymbol ?? "circle"
                      const currentFilled = selObj.markerFilled ?? true
                      return (
                        <>
                          {/* Filled version */}
                          <button
                            key={`${symbol}-filled`}
                            onClick={() => store.updateObject(selObj.layerId, selObj.id, { markerSymbol: symbol, markerFilled: true })}
                            className={`rounded p-1.5 transition-colors ${
                              currentSymbol === symbol && currentFilled
                                ? "bg-tool-active text-primary-foreground"
                                : "text-muted-foreground hover:bg-tool-hover"
                            }`}
                            title={`${symbol.charAt(0).toUpperCase() + symbol.slice(1)} (filled)`}
                          >
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                              {symbol === "circle" && <circle cx="8" cy="8" r="5" />}
                              {symbol === "rectangle" && <rect x="3" y="3" width="10" height="10" />}
                              {symbol === "diamond" && <polygon points="8,2 14,8 8,14 2,8" />}
                            </svg>
                          </button>
                          {/* Not-filled version */}
                          <button
                            key={`${symbol}-outline`}
                            onClick={() => store.updateObject(selObj.layerId, selObj.id, { markerSymbol: symbol, markerFilled: false })}
                            className={`rounded p-1.5 transition-colors ${
                              currentSymbol === symbol && !currentFilled
                                ? "bg-tool-active text-primary-foreground"
                                : "text-muted-foreground hover:bg-tool-hover"
                            }`}
                            title={`${symbol.charAt(0).toUpperCase() + symbol.slice(1)} (outline)`}
                          >
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                              {symbol === "circle" && <circle cx="8" cy="8" r="5" />}
                              {symbol === "rectangle" && <rect x="3" y="3" width="10" height="10" />}
                              {symbol === "diamond" && <polygon points="8,2 14,8 8,14 2,8" />}
                            </svg>
                          </button>
                        </>
                      )
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Probe-specific properties */}
            {selObj.type === "probe" && selObj.points.length >= 2 && (
              <div className="space-y-1">
                <span className="text-[10px] text-muted-foreground">Size: {selObj.probeSize ?? 90}px</span>
                <Slider
                  min={45} max={280} step={5}
                  value={[selObj.probeSize ?? 90]}
                  onValueChange={([v]) => {
                    // Update tail position to follow the new size
                    const head = selObj.points[0]
                    const tail = selObj.points[1]
                    const angle = Math.atan2(tail.y - head.y, tail.x - head.x)
                    // New tail position at distance = new probeSize from head
                    const newTail = {
                      x: head.x + Math.cos(angle) * v,
                      y: head.y + Math.sin(angle) * v,
                    }
                    store.updateObject(selObj.layerId, selObj.id, { 
                      probeSize: v,
                      points: [head, newTail]
                    })
                  }}
                  className="w-full"
                />
                <span className="text-[9px] text-muted-foreground/60">Drag body to move, tail vertex to rotate</span>
              </div>
            )}

            {/* Head/Tail symbols for open paths - only polyline and freehand, not comment/Point */}
            {["polyline", "freehand"].includes(selObj.type) && (() => {
              const endSymbolOptions: { id: LineEndSymbol; label: string }[] = [
                { id: "none", label: "—" },
                { id: "arrow", label: "→" },
                { id: "filled_arrow", label: "▶" },
                { id: "double_arrows", label: "»" },
                { id: "filled_square", label: "■" },
                { id: "filled_circle", label: "●" },
                { id: "losange", label: "◆" },
                { id: "chevron_right", label: "›" },
                { id: "chevron_left", label: "‹" },
                { id: "x", label: "✕" },
              ]
              return (
                <div className="space-y-1">
                  <span className="text-[10px] text-muted-foreground">End Symbols</span>
                  <div className="flex items-center gap-2">
                    <select
                      value={selObj.tailSymbol ?? "none"}
                      onChange={(e) => store.updateObject(selObj.layerId, selObj.id, { tailSymbol: e.target.value as LineEndSymbol })}
                      className="flex-1 rounded bg-secondary px-2 py-1 text-[11px] text-foreground outline-none border border-border text-center"
                      title="Tail symbol"
                    >
                      {endSymbolOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>{opt.label}</option>
                      ))}
                    </select>
                    <svg width="20" height="8" viewBox="0 0 20 8" className="shrink-0 text-muted-foreground">
                      <line x1="0" y1="4" x2="20" y2="4" stroke="currentColor" strokeWidth="1.5" />
                      <polygon points="16,1 20,4 16,7" fill="currentColor" />
                    </svg>
                    <select
                      value={selObj.headSymbol ?? "none"}
                      onChange={(e) => store.updateObject(selObj.layerId, selObj.id, { headSymbol: e.target.value as LineEndSymbol })}
                      className="flex-1 rounded bg-secondary px-2 py-1 text-[11px] text-foreground outline-none border border-border text-center"
                      title="Head symbol"
                    >
                      {endSymbolOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )
            })()}

            {/* Comment box rotation display - rotation is now controlled via drag handle */}
            {selObj.comment && (
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                <span>Comment Rotation: {selObj.commentRotation ?? 0}°</span>
                <button
                  onClick={() => store.updateObject(selObj.layerId, selObj.id, { commentRotation: 0 })}
                  className="text-[9px] px-1.5 py-0.5 rounded bg-secondary hover:bg-tool-hover transition-colors"
                  title="Reset rotation"
                >Reset</button>
              </div>
            )}

            {/* Text formatting for text objects and any object with a comment */}
            {(selObj.type === "text" || !!selObj.comment) && (
              <div className="space-y-2">
                <span className="text-[10px] text-muted-foreground">{selObj.type === "text" ? "Font" : "Comment Font"}</span>
                <select
                  value={selObj.fontFamily ?? "monospace"}
                  onChange={(e) => store.updateObject(selObj.layerId, selObj.id, { fontFamily: e.target.value })}
                  className="w-full rounded bg-secondary px-2 py-1 text-[10px] text-foreground outline-none border border-border"
                >
                  <option value="monospace">Monospace</option>
                  <option value="sans-serif">Sans-serif</option>
                  <option value="serif">Serif</option>
                  <option value="Arial">Arial</option>
                  <option value="Courier New">Courier New</option>
                </select>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-muted-foreground">Size</span>
                  <Slider
                    min={8} max={72} step={1}
                    value={[selObj.fontSize ?? (selObj.type === "text" ? Math.max(12, selObj.strokeWidth * 6) : 20)]}
                    onValueChange={([v]) => store.updateObject(selObj.layerId, selObj.id, { fontSize: v })}
                    className="flex-1"
                  />
                  <span className="text-[10px] font-mono text-muted-foreground w-6 text-right">
                    {selObj.fontSize ?? (selObj.type === "text" ? Math.max(12, selObj.strokeWidth * 6) : 20)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => store.updateObject(selObj.layerId, selObj.id, { fontBold: !selObj.fontBold })}
                    className={`rounded px-2 py-0.5 text-[10px] font-bold transition-colors ${selObj.fontBold ? "bg-tool-active text-primary-foreground" : "text-muted-foreground hover:bg-tool-hover"}`}
                  >B</button>
                  <button
                    onClick={() => store.updateObject(selObj.layerId, selObj.id, { fontItalic: !selObj.fontItalic })}
                    className={`rounded px-2 py-0.5 text-[10px] italic transition-colors ${selObj.fontItalic ? "bg-tool-active text-primary-foreground" : "text-muted-foreground hover:bg-tool-hover"}`}
                  >I</button>
                  <button
                    onClick={() => store.updateObject(selObj.layerId, selObj.id, { fontUnderline: !selObj.fontUnderline })}
                    className={`rounded px-2 py-0.5 text-[10px] underline transition-colors ${selObj.fontUnderline ? "bg-tool-active text-primary-foreground" : "text-muted-foreground hover:bg-tool-hover"}`}
                  >U</button>
                </div>
              </div>
            )}

            {/* Quick actions */}
            <div className="flex items-center gap-1 pt-1 border-t border-border">
              <button
                onClick={() => {
                  store.removeObject(selObj.layerId, selObj.id)
                  setSelectedObjectId(null); setSelectedLayerId(null)
                }}
                className="rounded px-2 py-1 text-[10px] text-destructive hover:bg-destructive/10 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No image placeholder */}
      {!store.pcbImage && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center space-y-2">
            <div className="text-muted-foreground/30 text-sm font-mono">Import a PCB image to begin</div>
            <div className="text-muted-foreground/20 text-xs">Use the Image button in the toolbar</div>
          </div>
        </div>
      )}

      {/* Cursor crosshair info */}
      {mouseCanvas && store.activeTool !== "select" && (
        <div className="absolute bottom-2 left-2 rounded bg-secondary/80 px-2 py-0.5 text-[10px] font-mono text-muted-foreground backdrop-blur-sm">
          {Math.round(mouseCanvas.x)}, {Math.round(mouseCanvas.y)}
        </div>
      )}
    </div>
  )
}
